// Acceptations des règles de publication — le membre doit cocher « J'ai lu et j'accepte de
// respecter la charte de Voissa » avant de publier son premier média et son premier commentaire.
// Miroir de services/userAcceptance/userAcceptance.service.js (voissa-api-rest).
//
// RÉPARTITION DES RÔLES — l'API sert, pour chaque type, la DATE d'acceptation ou `null` dans
// `currentUser.acceptances` (cf. GET /users/me) ; ce module ne fait que la lire. Aucune règle
// métier n'est réécrite ici : ajouter un type côté API suffit, il apparaît dans le payload.
//
// L'API reste seule juge : elle refuse en 403 avec le code ci-dessous, et le front n'anticipe ce
// refus que pour ouvrir la modale AVANT la requête — donc sans faire perdre la saisie en cours.

// Types d'acceptation. Valeurs figées, partagées avec l'API (const/userAcceptance.js).
export const ACCEPTANCE_TYPES = {
    MEDIA: 'media-publication',
    COMMENT: 'comment-publication',
};

// Contrat avec l'API : `errors.code` du payload 403.
export const ACCEPTANCE_ERROR_CODE = 'ACCEPTANCE_REQUIRED';

/**
 * Date d'acceptation d'un type, ou `null`.
 *
 * ⚠️ `undefined` (payload absent) et `null` (jamais accepté) ne veulent PAS dire la même chose,
 * d'où `isAcceptanceRequired` ci-dessous : sans information, on ne bloque pas.
 */
export const getAcceptanceDate = (currentUser, type) => currentUser?.acceptances?.[type] ?? null;

/**
 * Faut-il demander l'acceptation avant d'exécuter l'action ?
 *
 * Trois cas où l'on répond NON alors même qu'aucune date n'est connue :
 *   - INVITÉ : son parcours est l'inscription, pas la charte. Les CTA le renvoient déjà vers la
 *     modale de connexion ; lui présenter des règles de publication n'aurait aucun sens.
 *   - payload ABSENT (première frame avec GET /users/me en vol, session ouverte avant la mise en
 *     service, front déployé avant l'API) : sans information on laisse passer, l'API tranchera et
 *     le filet global ouvrira la modale sur son refus. Bloquer par défaut aurait rendu le site
 *     muet pour tout le monde le jour où le front part avant l'API.
 *   - TYPE INCONNU du payload : même raisonnement — un type que l'API ne connaît pas encore ne
 *     doit pas fermer une publication qui marchait hier.
 */
export const isAcceptanceRequired = (currentUser, type) => {
    if (!currentUser?._id) return false;
    if (!currentUser?.acceptances) return false;
    if (!(type in currentUser.acceptances)) return false;
    return !getAcceptanceDate(currentUser, type);
};

/**
 * Reconnaît le refus de l'API, quelle que soit la forme de l'erreur reçue : mutation
 * (`error.response.errors`), query (`error.errors`) ou axios brut (`error.response.data`).
 * Cf. src/interceptors/data.js, qui normalise différemment les deux premiers cas.
 */
export const isAcceptanceError = (error) => {
    const codes = [
        error?.errors?.code,
        error?.response?.errors?.code,
        error?.response?.data?.errors?.code,
    ];
    return codes.includes(ACCEPTANCE_ERROR_CODE);
};

/** Type visé par un refus 403, pour ouvrir la bonne modale sans avoir à deviner depuis la route. */
export const getAcceptanceErrorType = (error) => (
    error?.errors?.acceptance?.type
    || error?.response?.errors?.acceptance?.type
    || error?.response?.data?.errors?.acceptance?.type
    || null
);

// --- Filet de sécurité global -----------------------------------------------------------------
// Tous les chemins de publication ne sont pas gatés au clic (client non à jour, cache
// `currentUser` antérieur à la mise en service…). Quand l'API refuse malgré tout, le MutationCache
// émet ici et le listener monté sous le ModalManager ouvre la même modale. Un abonné
// module-level : l'émetteur (cache React Query) vit hors de l'arbre React et n'a pas accès au
// contexte des modales.
//
// ⚠️ Cette voie-là ne rejoue PAS l'action : la requête a déjà échoué et son contexte est perdu.
// Le membre accepte, puis reclique — d'où l'intérêt de gater au clic partout où c'est possible.
let listeners = [];

export const subscribeAcceptance = (listener) => {
    listeners = [...listeners, listener];
    return () => {
        listeners = listeners.filter((item) => item !== listener);
    };
};

export const emitAcceptance = (type) => {
    listeners.forEach((listener) => listener(type));
};
